import type { DeepKeys, DeepValue, Updater } from "@tanstack/react-form";
import type { z } from "zod";

import LabeledField from "@/components/form/labeled-field";
import type { RadioGroupCustomProps } from "@/components/radio-group-custom";
import { RadioGroupCustom } from "@/components/radio-group-custom";
import type { FieldBaseProps } from "@/types/form";
import { getOnChangeFromZod } from "@/utils/form";

export type FieldRadioProps<
  TFormData,
  TName extends DeepKeys<TFormData> & string,
  TSchema extends z.ZodType<TFormData> | undefined,
  TData extends DeepValue<TFormData, TName> = DeepValue<TFormData, TName>,
> = FieldBaseProps<TFormData, TName, TSchema, TData> & {
  orientation?: "horizontal" | "vertical";
  type?: "string" | "boolean";
} & RadioGroupCustomProps;

const FieldRadio = <
  TFormData,
  TName extends DeepKeys<TFormData> & string,
  TSchema extends z.ZodType<TFormData> | undefined,
  TData extends DeepValue<TFormData, TName> = DeepValue<TFormData, TName>,
>({
  Field,
  name,
  schema,
  label,
  onValueChange,
  options,
  orientation = "vertical",
  type = "string",
  labelProps,
  gridRow,
  ...props
}: FieldRadioProps<TFormData, TName, TSchema, TData>) => {
  return (
    <Field
      name={name}
      validators={{
        onChange: schema ? getOnChangeFromZod(schema, name) : undefined,
      }}
      children={(field) => {
        return (
          <LabeledField
            label={label}
            fieldName={field.name.toString()}
            errors={field.state.meta.errors}
            required={props.required}
            gridRow={gridRow}
            {...labelProps}
          >
            <RadioGroupCustom
              id={name}
              className={
                orientation === "horizontal"
                  ? "flex align-center flex-col sm:flex-row gap-x-6 gap-y-2 w-min h-min"
                  : ""
              }
              options={options}
              value={
                type === "boolean" && field.state.value !== null
                  ? (field.state.value as boolean | null) === true
                    ? "true"
                    : "false"
                  : ((field.state.value as string | null) ?? "")
              }
              onValueChange={(value) => {
                if (onValueChange) onValueChange(value);
                (
                  field.handleChange as (
                    updater: Updater<string | boolean | null>
                  ) => void
                )(type === "boolean" ? value === "true" : value);
              }}
              {...props}
            />
          </LabeledField>
        );
      }}
    />
  );
};

export default FieldRadio;
